
import { useRef, useState, MouseEvent } from "react";
import { capitalize } from "../utils";
import { XIcon } from "@heroicons/react/outline";
import { Region } from "../interfaces";
import { PDFDocumentProxy } from "pdfjs-dist";

interface Props {
  pdfDocument: PDFDocumentProxy;
  pageNumber: number;
  regions?: Array<Region>;
  updateRegions?: ((regions: Array<Region>) => void);
  disabled?: boolean;
}

const COLOUR_MAP: Record<string, string> = {
  1: "bg-blue/40",
  2: "bg-purple/40",
  3: "bg-green/40",
  4: "bg-red/40",
}

function PDFViewerOverlay(props: Props) {
  const pageContainerRef = useRef<HTMLDivElement>(null);

  const [currentRegionId, setCurrentRegionId] = useState<string>();
  const [currentOffset, setCurrentOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

  const [isResizing, setIsResizing] = useState(false);

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    if (!currentRegionId || !props.regions || !props.updateRegions)
      return;

    const overlayRect = pageContainerRef.current?.getBoundingClientRect()!;
    const overlayHeight = overlayRect.height;

    const localX = e.clientX - overlayRect?.left;
    const localY = e.clientY - overlayRect?.top;

    const updatedRegions = [...props.regions];

    // TODO: We should store a map for this instead
    const regionIndex = updatedRegions.findIndex(r => r.id === currentRegionId);
    if (regionIndex === -1)
      return;

    const currentRegion = updatedRegions[regionIndex];

    if (isResizing) {
      let width, height;

      if (currentRegion.type === "CHECKBOX") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 20), 400);
        height = width;
      } else if (currentRegion.type === "SIGNATURE") {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 120), 800);
        height = width * 0.2;
      } else {
        width = Math.min(Math.max(localX - currentRegion.rect.x, 40), 800);
        height = Math.max(width * 0.2, 20);
      }

      updatedRegions[regionIndex] = { ...currentRegion, rect: { ...currentRegion.rect, width, height } };
    }
    else {
      const x = localX - currentOffset.x;
      const y = localY - currentOffset.y;

      if (y < 0 && currentRegion.pageNumber > 1) {
        updatedRegions[regionIndex] = {
          ...currentRegion,
          pageNumber: Math.max(currentRegion.pageNumber - 1, 1),
          rect: {
            ...currentRegion.rect, x, y: overlayHeight - currentRegion.rect.height - 20
          }
        };
        setCurrentRegionId(undefined);

      } else if (y + currentRegion.rect.height > overlayHeight && currentRegion.pageNumber < props.pdfDocument.numPages) {
        updatedRegions[regionIndex] = {
          ...currentRegion,
          pageNumber: Math.min(currentRegion.pageNumber + 1, props.pdfDocument.numPages),
          rect: {
            ...currentRegion.rect, x, y: 20
          }
        };
        setCurrentRegionId(undefined);

      } else {
        updatedRegions[regionIndex] = { ...currentRegion, rect: { ...currentRegion.rect, x, y } };
      }
    }

    props.updateRegions(updatedRegions);
  }

  function handleMouseDown(e: MouseEvent<HTMLDivElement>, regionId: string, opts = { isResizing: false }) {
    if (props.disabled)
      return;

    const rect = e.currentTarget.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCurrentOffset({ x, y });
    setCurrentRegionId(regionId);
    setIsResizing(opts.isResizing);

    e.preventDefault();
    e.stopPropagation();
  }

  function handleDeleteRegion(e: MouseEvent<HTMLDivElement>, regionId: string) {
    if (!props.regions || !props.updateRegions)
      return;

    const updatedRegions = props.regions.filter(r => r.id !== regionId);
    props.updateRegions(updatedRegions);
  }

  return (
    <div className={`absolute left-0 top-0 w-full h-full`} ref={pageContainerRef} onMouseMove={handleMouseMove} onMouseUp={() => setCurrentRegionId(undefined)}>
      {(props?.regions || [])
        .filter(r => r.pageNumber === props.pageNumber)
        .map(region => (
          <div key={region.id} className={`box-border absolute select-none flex items-center justify-between p-2 rounded text-white ${props.disabled ? "cursor-default" : ((currentRegionId === region.id ? "cursor-grabbing" : "cursor-grab") + " group")} ${COLOUR_MAP[region.recipient]}`} style={{ "left": region.rect.x, "top": region.rect.y, width: region.rect.width, height: region.rect.height }} onMouseDown={(e) => handleMouseDown(e, region.id)}>
            {region.type !== "CHECKBOX" &&
              <>
                <div className="font-semibold font-gilroy">
                  {region.placeholder || capitalize(region.rect.width < 100 ? (region.type.slice(0, 1) + ".") : region.type)}
                  {region.required &&
                    <span className="text-red">*</span>
                  }
                </div>
                {region.rect.height >= 32
                  ? <div className="font-gilroy font-semibold w-6 h-6 rounded-full border-2 border-white flex items-center justify-center">{region.recipient}</div>
                  : <div className="font-gilroy font-semibold w-4 h-4 rounded-full border-0 border-white flex items-center justify-center">{region.recipient}</div>
                }
              </>
            }

            {(region.type === "CHECKBOX" && region.rect.width >= 20) &&
              <div className="flex items-center justify-center w-full h-full">
                <div className="font-gilroy font-semibold w-4 h-4 -m-2 rounded border-2 border-white flex items-center justify-center text-xs relative overflow-visible">
                  {region.recipient}
                  {region.required &&
                    <span className="text-red absolute -right-1 -top-1.5 text-sm">*</span>
                  }
                </div>
              </div>
            }

            <div className="absolute -right-2 -top-2 cursor-default" style={{ width: 24, height: region.rect.height + 20 }} />
            <div className={`flex items-center justify-center rounded-full bg-red border-white border-2 -right-2 -top-2 absolute w-4 h-4 shadow cursor-pointer invisible group-hover:visible`} onClick={(e) => handleDeleteRegion(e, region.id)}>
              <XIcon className="w-4 h-4" />
            </div>
            <div className={`rounded-full bg-gray-400 border-white border-2 -right-2 -bottom-2 absolute w-4 h-4 shadow cursor-nwse-resize ${(isResizing && currentRegionId === region.id) ? "visible" : "invisible group-hover:visible"}`} onMouseDown={(e) => handleMouseDown(e, region.id, { isResizing: true })} />
          </div>
        ))}
    </div>
  )

}

export default PDFViewerOverlay;